import type { Translations } from '../types';

export const en: Translations = {
  common: {
    home: 'Home',
    github: 'GitHub',
    copy: 'Copy',
    clear: 'Clear',
    input: 'Input',
    output: 'Output',
    encode: 'Encode',
    decode: 'Decode',
    generate: 'Generate',
    convert: 'Convert',
    result: 'Result',
    copiedToClipboard: 'Copied to clipboard!',
    failedToCopy: 'Failed to copy',
    language: 'Language',
  },
  seo: {
    siteDescription: 'Free, privacy-focused developer tools collection. JSON formatter, Base64 encoder/decoder, UUID generator, hash generator, URL encoder, timestamp converter, and color converter. All tools work offline in your browser.',
    keywords: [
      'developer tools',
      'json formatter',
      'base64 encoder',
      'uuid generator',
      'hash generator',
      'url encoder',
      'timestamp converter',
      'color converter',
      'online tools',
      'free tools',
      'privacy focused',
      'offline tools',
    ],
    ogTitle: 'GetDev.Tools - Free Privacy-Focused Developer Tools',
    ogDescription: 'Collection of free developer tools that work offline in your browser. JSON formatter, Base64, UUID, Hash, URL encoder, and more.',
  },
  home: {
    title: 'Developer Tools Collection',
    subtitle: 'Free, privacy-focused tools for developers. All processing happens in your browser.',
    whyTitle: 'Why GetDev.Tools?',
    privacyTitle: '🔒 Privacy First',
    privacyDesc: 'All processing happens in your browser. Your data never leaves your device.',
    fastTitle: '⚡ Lightning Fast',
    fastDesc: 'No server roundtrips. Everything runs locally for instant results.',
    freeTitle: '🆓 Free Forever',
    freeDesc: 'Open source and free to use. No registration or payment required.',
    footer: '© 2025 GetDev.Tools - Privacy-focused developer tools',
    footerSubtitle: 'All processing happens in your browser',
  },
  tools: {
    jsonFormatter: {
      name: 'JSON Formatter',
      description: 'Format, validate, and beautify JSON data',
      format: 'Format',
      minify: 'Minify',
      validate: 'Validate',
      error: 'Error',
    },
    base64: {
      name: 'Base64 Encoder/Decoder',
      description: 'Encode and decode Base64 strings',
      encodeButton: 'Encode',
      decodeButton: 'Decode',
    },
    uuid: {
      name: 'UUID Generator',
      description: 'Generate RFC 4122 compliant UUID v4 identifiers',
      numberOfUuids: 'Number of UUIDs to generate',
      generatedUuids: 'Generated UUIDs',
      copyAll: 'Copy All',
      aboutTitle: 'About UUID v4',
      aboutDesc: 'UUID (Universally Unique Identifier) v4 are randomly generated 128-bit identifiers. The probability of collision is extremely low, making them suitable for distributed systems without central coordination.',
    },
    hash: {
      name: 'Hash Generator',
      description: 'Generate SHA-1, SHA-256, SHA-512 hashes',
      inputText: 'Input Text',
      securityNotice: '⚠️ Security Notice',
      securityDesc: 'SHA-1 is deprecated for security purposes. Use SHA-256 or SHA-512 for cryptographic applications. MD5 is not available in modern browsers due to security vulnerabilities.',
    },
    urlEncoder: {
      name: 'URL Encoder/Decoder',
      description: 'Encode and decode URL strings',
      encodeUrl: 'Encode URL',
      encodeComponent: 'Encode Component',
      encoded: 'Encoded',
      decoded: 'Decoded',
      differenceTitle: '💡 Difference',
      encodeUriDesc: 'encodeURI: Encodes a full URI (doesn\'t encode: ; , / ? : @ & = + $ #)',
      encodeComponentDesc: 'encodeURIComponent: Encodes URI component (encodes all special characters)',
      examples: 'Examples',
    },
    timestamp: {
      name: 'Timestamp Converter',
      description: 'Convert between Unix timestamps and human-readable dates',
      currentTime: 'Current Time',
      unixMs: 'Unix Timestamp (ms)',
      unixS: 'Unix Timestamp (s)',
      toDate: 'Timestamp → Date',
      toTimestamp: 'Date → Timestamp',
      useNow: 'Use Now',
      aboutTitle: '💡 About Unix Timestamps',
      aboutDesc1: '• Unix timestamp = seconds since Jan 1, 1970 00:00:00 UTC (epoch)',
      aboutDesc2: '• 10 digits = seconds (e.g., 1609459200)',
      aboutDesc3: '• 13 digits = milliseconds (e.g., 1609459200000)',
      aboutDesc4: '• Supported date formats: ISO 8601, RFC 2822, or natural language',
    },
    color: {
      name: 'Color Converter',
      description: 'Convert between HEX, RGB, and HSL formats',
      pickColor: 'Pick a Color',
      formatGuide: '💡 Color Format Guide',
      hexDesc: 'HEX: Hexadecimal notation (#RRGGBB)',
      rgbDesc: 'RGB: Red, Green, Blue values (0-255)',
      hslDesc: 'HSL: Hue (0-360°), Saturation (0-100%), Lightness (0-100%)',
      alphaDesc: 'RGBA/HSLA: Same as RGB/HSL with Alpha (opacity) channel',
    },
    jwt: {
      name: 'JWT Decoder',
      description: 'Decode and inspect JSON Web Tokens',
      inputToken: 'JWT Token',
      header: 'Header',
      payload: 'Payload',
      signature: 'Signature (Base64)',
      claims: 'Standard Claims',
      invalidFormat: 'Invalid JWT format. Token must have 3 parts separated by dots.',
      invalidToken: 'Invalid JWT token',
      securityNotice: 'Security Notice',
      securityDesc: 'This tool only decodes JWT tokens. It does NOT verify signatures. Never paste sensitive tokens from production systems.',
      aboutTitle: '💡 About JWT',
      aboutDesc1: 'JWT = JSON Web Token, used for secure authentication',
      aboutDesc2: 'Format: header.payload.signature (Base64 encoded)',
      aboutDesc3: 'Header contains algorithm and token type, Payload contains claims',
    },
    diff: {
      name: 'Text Diff',
      description: 'Compare two text blocks and see differences',
      originalText: 'Original Text',
      modifiedText: 'Modified Text',
      sideBySide: 'Side by Side',
      unified: 'Unified',
      noDifference: 'No differences found',
    },
    markdown: {
      name: 'Markdown Preview',
      description: 'Write and preview Markdown in real-time',
      editor: 'Editor',
      preview: 'Preview',
      supportedFeatures: 'Supported: Headers, Lists, Links, Images, Code Blocks, Tables, and more',
    },
    regex: {
      name: 'Regex Tester',
      description: 'Test regular expressions and see matches',
      pattern: 'Regular Expression',
      flags: 'Flags',
      testString: 'Test String',
      matches: 'Matches',
      noMatches: 'No matches found',
      invalidRegex: 'Invalid regular expression',
      globalFlag: 'Global (g)',
      caseInsensitive: 'Case Insensitive (i)',
      multiline: 'Multiline (m)',
      dotAll: 'Dot matches newline (s)',
    },
    qrCode: {
      name: 'QR Code Generator',
      description: 'Generate QR codes for URLs, text, WiFi, and more',
      inputData: 'Input Data',
      qrCodeImage: 'QR Code Image',
      size: 'Size (pixels)',
      errorCorrection: 'Error Correction',
      errorLow: 'Low (~7%)',
      errorMedium: 'Medium (~15%)',
      errorQuartile: 'Quartile (~25%)',
      errorHigh: 'High (~30%)',
      templates: 'Quick Templates',
      templateUrl: 'Website URL',
      templateEmail: 'Email',
      templatePhone: 'Phone Number',
      templateSms: 'SMS',
      templateWifi: 'WiFi Network',
      templateVcard: 'Contact Card (vCard)',
      privacyNotice: '🔒 Privacy Notice',
      privacyDesc: 'QR codes are generated using an external API (qrserver.com). Your data will be sent to their server.',
    },
    lorem: {
      name: 'Lorem Ipsum Generator',
      description: 'Generate placeholder text for designs and mockups',
      generateType: 'Generate Type',
      paragraphs: 'Paragraphs',
      sentences: 'Sentences',
      words: 'Words',
      count: 'Count',
      startWithLorem: 'Start with "Lorem ipsum"',
      generatedText: 'Generated Text',
      aboutTitle: '💡 About Lorem Ipsum',
      aboutDesc: 'Lorem Ipsum is placeholder text commonly used in design and publishing. It approximates the letter frequency of English, making it look like readable text.',
    },
    htmlEntity: {
      name: 'HTML Entity Encoder',
      description: 'Encode and decode HTML entities',
      inputText: 'Input Text',
      encodeBasic: 'Encode (Basic)',
      encodeAll: 'Encode All',
      decode: 'Decode',
      commonEntities: 'Common HTML Entities',
      character: 'Character',
      entity: 'Entity',
      description2: 'Description',
      useCases: '💡 Use Cases',
      useCase1: 'Encode Basic: Only encodes essential characters (<, >, &, ", \')',
      useCase2: 'Encode All: Encodes all special characters for maximum safety',
      useCase3: 'Decode: Converts HTML entities back to normal characters',
    },
  },
};